<script setup>
import { computed } from 'vue'

/*
 * 인라인 SVG 아이콘.
 * 사용자 사이트에는 아이콘 폰트 의존성이 없다. 폰트 파일 하나를 더 받게 하는 대신
 * 실제로 쓰는 아이콘만 이 맵에 두고 currentColor 로 색을 상속받는다.
 */

const props = defineProps({
  name: { type: String, required: true },
  size: { type: [Number, String], default: 20 },
  strokeWidth: { type: [Number, String], default: 1.8 },
})

// 모두 24x24 좌표계의 선 아이콘이다. d 배열은 <path>, 그 외는 별도 요소로 그린다.
const ICONS = {
  chat: { paths: ['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'] },
  monitor: {
    paths: ['M8 21h8', 'M12 17v4'],
    rects: [{ x: 2, y: 3, w: 20, h: 14, r: 2 }],
  },
  folder: {
    paths: [
      'M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z',
    ],
  },
  building: {
    paths: [
      'M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18',
      'M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-2',
      'M10 6h4',
      'M10 10h4',
      'M10 14h4',
    ],
  },
  menu: { paths: ['M4 6h16', 'M4 12h16', 'M4 18h16'] },
  close: { paths: ['M18 6 6 18', 'M6 6l12 12'] },
  user: {
    paths: ['M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2'],
    circles: [{ cx: 12, cy: 7, r: 4 }],
  },
  lock: {
    paths: ['M7 11V7a5 5 0 0 1 10 0v4'],
    rects: [{ x: 3, y: 11, w: 18, h: 11, r: 2 }],
  },
  headset: {
    paths: [
      'M3 18v-6a9 9 0 0 1 18 0v6',
      'M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z',
      'M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z',
    ],
  },
  chevronLeft: { paths: ['m15 18-6-6 6-6'] },
  chevronRight: { paths: ['m9 18 6-6-6-6'] },
  arrowRight: { paths: ['M5 12h14', 'm12 5 7 7-7 7'] },
  arrowUp: { paths: ['m18 15-6-6-6 6'] },
  shield: {
    paths: [
      'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
      'm9 12 2 2 4-4',
    ],
  },
  book: { paths: ['M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20'] },
  hardHat: {
    paths: [
      'M2 18a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-1a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1z',
      'M10 10V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5',
      'M4 16v-4a6 6 0 0 1 6-6',
      'M14 6a6 6 0 0 1 6 6v4',
    ],
  },
  clock: { paths: ['M12 6v6l4 2'], circles: [{ cx: 12, cy: 12, r: 10 }] },
  megaphone: { paths: ['m3 11 18-5v12L3 14v-3z', 'M11.6 16.8a3 3 0 1 1-5.8-1.6'] },
  graduation: { paths: ['M22 10v6', 'M2 10l10-5 10 5-10 5z', 'M6 12v5c3 3 9 3 12 0v-5'] },
  mail: { paths: ['m22 7-10 5L2 7'], rects: [{ x: 2, y: 4, w: 20, h: 16, r: 2 }] },
  download: { paths: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'm7 10 5 5 5-5', 'M12 15V3'] },
  play: { paths: ['m10 8 6 4-6 4z'], circles: [{ cx: 12, cy: 12, r: 10 }] },
  spark: { paths: ['M12 3v4', 'M12 17v4', 'M3 12h4', 'M17 12h4', 'm5.6 5.6 2.9 2.9', 'm15.5 15.5 2.9 2.9', 'm18.4 5.6-2.9 2.9', 'm8.5 15.5-2.9 2.9'] },
  search: { paths: ['m21 21-4.34-4.34'], circles: [{ cx: 11, cy: 11, r: 8 }] },
  pencil: {
    paths: [
      'M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z',
      'm15 5 4 4',
    ],
  },
  trash: { paths: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6', 'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2', 'M10 11v6', 'M14 11v6'] },
  reply: { paths: ['M9 17H4V7', 'M4 17c0-6 4-9 10-9h6', 'm15 4 5 4-5 4'] },
  paperclip: { paths: ['m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48'] },
  image: {
    paths: ['m21 15-3.09-3.09a2 2 0 0 0-2.82 0L6 21'],
    rects: [{ x: 3, y: 3, w: 18, h: 18, r: 2 }],
    circles: [{ cx: 9, cy: 9, r: 2 }],
  },
}

const icon = computed(() => ICONS[props.name] || ICONS.spark)
</script>

<template>
  <svg
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <rect
      v-for="(rect, index) in icon.rects || []"
      :key="`r${index}`"
      :x="rect.x"
      :y="rect.y"
      :width="rect.w"
      :height="rect.h"
      :rx="rect.r"
    />
    <circle
      v-for="(circle, index) in icon.circles || []"
      :key="`c${index}`"
      :cx="circle.cx"
      :cy="circle.cy"
      :r="circle.r"
    />
    <path v-for="(d, index) in icon.paths || []" :key="`p${index}`" :d="d" />
  </svg>
</template>
